import type { ApplicationRecord, ProfileRecord } from '../api/types'

/** Has this person actually applied? */
export const isSubmitted = (app?: ApplicationRecord | null): boolean =>
  Boolean(app && (app.status === 'submitted' || app.status === 'reviewed'))

/**
 * Does this profile still owe onboarding?
 *
 * Note the null case. Anything that is not explicitly 'completed' means
 * onboarding is still owed — a brand-new profile is created without a phase,
 * and treating null as "done" silently skipped onboarding for every new member.
 *
 * A SUBMITTED APPLICATION ALSO COUNTS AS DONE, and leaving that out was a bug.
 * `isRegisteredMember` below has always accepted either signal, so a member
 * whose questionnaire was submitted but whose `onboarding_phase` never reached
 * 'completed' came out BOTH registered and still-owing-onboarding. App.tsx
 * trusted the second, sent them back into the flow, and the resume logic —
 * "first unanswered question", of which there are none — dropped them on the
 * LAST question every time they signed in.
 *
 * That state was easy to reach: `updateOnboarding` was posting the wrong field
 * name, so the phase PATCH 400'd after the answers had already saved. The two
 * definitions must agree, or a failure in one write leaves people stuck.
 */
export const needsOnboarding = (
  profile?: ProfileRecord | null,
  application?: ApplicationRecord | null
): boolean =>
  Boolean(profile)
  && profile!.onboarding_phase !== 'completed'
  && !isSubmitted(application)

/**
 * IS THIS PERSON ACTUALLY REGISTERED?
 *
 * "Has a profile row" is NOT the answer: on the mini program a row is created
 * silently on first launch, and on the web an SMS code creates one before the
 * questionnaire is touched. Registered means the two things the person chose
 * to do: proved who they are, and finished onboarding.
 *
 * `identityDone` is the per-platform half — WeChat profile granted in the mini
 * program, phone verified on the web — passed in so this stays platform-free.
 */
export const isRegisteredMember = (
  profile?: ProfileRecord | null,
  application?: ApplicationRecord | null,
  identityDone: boolean = Boolean(profile?.wechat_profile_granted_at || profile?.phone_number)
): boolean =>
  Boolean(profile) && identityDone
  && (profile!.onboarding_phase === 'completed' || isSubmitted(application))

/** Tabs that only open once someone has applied. */
export const GATED_TABS = ['dining', 'six'] as const

export const canOpenTab = (tab: string, application?: ApplicationRecord | null): boolean =>
  !(GATED_TABS as readonly string[]).includes(tab) || isSubmitted(application)

/**
 * THE landing tab. Social Dining is home for a member; anyone who has not
 * applied has nothing to see there, so Apply is home for them.
 */
export const homeTab = (application?: ApplicationRecord | null): 'dining' | 'apply' =>
  isSubmitted(application) ? 'dining' : 'apply'

export type MemberPhase = 'guest' | 'incomplete' | 'member'

/**
 * Three honest answers, not two.
 *
 *   guest       no session — never signed in, or signed out. Identical states.
 *   incomplete  signed in, onboarding unfinished. Real and reachable.
 *   member      signed in and onboarded.
 */
export const memberPhase = (
  hasSession: boolean,
  profile?: ProfileRecord | null,
  application?: ApplicationRecord | null
): MemberPhase => {
  if (!hasSession) return 'guest'
  return isRegisteredMember(profile, application) ? 'member' : 'incomplete'
}
